import { Link } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import { Check, ChevronDown, LayoutGrid } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
} from '@/components/ui/DropdownMenu';
import { Text } from '@/components/ui/Text';

export interface ToolOption {
  id: string;
  label: string;
  /** One line under the label: what the tool is for. */
  description: string;
  /** Where the tool opens. */
  to: string;
  icon: LucideIcon;
}

export interface ToolSwitcherProps {
  /** The tools this caller may open. The caller filters them by permission before passing them. */
  tools: readonly ToolOption[];
  /** The id of the tool the current page belongs to. */
  currentId: string;
}

/**
 * Switches between the app's tools — the main Entiti workspace and the Agency CRM (ENT-565) — in the
 * top bar, beside the context switcher. Each tool has its own sidebar; this menu is how the user moves
 * between them. Renders nothing when the caller can open only one tool, so a user without CRM access
 * never sees it.
 */
export function ToolSwitcher({ tools, currentId }: ToolSwitcherProps) {
  if (tools.length < 2) return null;
  const current = tools.find((t) => t.id === currentId) ?? tools[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-semibold text-text hover:bg-surface-muted hover:text-primary transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={`Switch tool, current tool ${current.label}`}
      >
        <LayoutGrid size={14} className="text-text-subtle" />
        <span>{current.label}</span>
        <ChevronDown size={13} className="text-text-subtle" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[260px]">
        <DropdownMenuLabel>
          <Text variant="label-sm" color="muted">Tools</Text>
        </DropdownMenuLabel>
        {tools.map(({ id, label, description, to, icon: Icon }) => (
          <DropdownMenuItem key={id} asChild>
            <Link to={to} className="flex items-start gap-3" aria-current={id === current.id ? 'page' : undefined}>
              <Icon size={16} className="mt-0.5 shrink-0 text-primary" />
              <span className="flex min-w-0 flex-1 flex-col">
                <Text variant="body-sm" as="span" className="font-semibold">{label}</Text>
                <Text variant="body-sm" as="span" color="muted">{description}</Text>
              </span>
              {id === current.id && <Check size={14} className="mt-0.5 shrink-0 text-primary" />}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
